import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, Eye, MonitorPlay, ShieldCheck, X } from "lucide-react";
import { apiFetch } from "@/lib/api";

/**
 * Optional customer-owned live cloud browser view inside an EARNOS mission.
 * Never fetches or displays provider CDP URLs, customer credentials or an
 * iframe for an unapproved session. Real screen only; no fake animation.
 */
export default function WorkLiveBrowser({ handoff, missionId }) {
  const [open, setOpen] = useState(false);
  const [live, setLive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const request = useRef(0);
  const [now, setNow] = useState(Date.now);
  const sid = handoff?.session_id;
  const expires = handoff?.expires_at;
  const ownsView = live?.session_id === sid && live?.mission_id === missionId;
  const deadline = Math.min(Date.parse(expires), ownsView ? Date.parse(live.expires_at) : Infinity);
  const expired = !Number.isFinite(deadline) || deadline <= now;

  function close() {
    request.current += 1;
    setOpen(false); setLive(null); setBusy(false);
  }

  useEffect(() => {
    request.current += 1;
    setOpen(false); setLive(null); setBusy(false); setError(""); setNow(Date.now());
    return () => { request.current += 1; };
  }, [sid, missionId, expires]);

  useEffect(() => {
    if (!Number.isFinite(deadline)) return;
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      request.current += 1;
      setOpen(false); setLive(value => value ? {...value, url: ""} : null); setBusy(false); setNow(Date.now());
      return;
    }
    const timer = setTimeout(() => {
      request.current += 1;
      setOpen(false); setLive(value => value ? {...value, url: ""} : null); setBusy(false); setNow(Date.now());
    }, Math.min(remaining, 2147483647));
    return () => clearTimeout(timer);
  }, [deadline]);

  async function show() {
    if (open) { close(); return; }
    if (!sid || expired || busy) return;
    const currentRequest = ++request.current;
    setBusy(true); setError("");
    try {
      // Authenticated owner-only endpoint returns only the session's
      // provider-issued fullscreen LIVE VIEW (never its encrypted CDP URL).
      const response = await apiFetch("/browser-host/login-session/" +
        encodeURIComponent(sid) + "/live");
      if (currentRequest !== request.current) return;
      const u = new URL(response.live_url);
      if (u.protocol !== "https:" || !u.hostname || u.username || u.password ||
          response.session_id !== sid || !Number.isFinite(Date.parse(response.expires_at)) || Date.parse(response.expires_at) <= Date.now()) {
        throw new Error("Secure live browser view could not be verified");
      }
      if (Date.parse(expires) <= Date.now()) throw new Error("Browser session expired. Start a new approved session.");
      setLive({url: response.live_url, expires_at: response.expires_at, session_id: sid, mission_id: missionId});
      setOpen(true);
    } catch (e) {
      if (currentRequest === request.current) setError(e.message || "Live browser unavailable. The session might have expired.");
    } finally { if (currentRequest === request.current) setBusy(false); }
  }

  if (!sid) return null;
  return <section className="rounded-2xl border border-brand/30 bg-surface-2 p-3 space-y-3"
    aria-label="Customer-owned live browser" data-testid="earnos-work-live-browser">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-2 min-w-0">
        <MonitorPlay size={20} className="shrink-0 text-brand-light mt-0.5"/>
        <div>
          <p className="font-bold text-ink text-sm">Live browser screen</p>
          <p className="text-xs text-ink-2">Your authorized cloud session · {expired ? "expired" : "viewable while active"}</p>
        </div>
      </div>
      {open ? <button type="button" aria-label="Close browser view" onClick={close}
        className="p-2 rounded-lg border border-line text-ink"><X size={17}/></button> : null}
    </div>
    {expired ? <p className="text-xs text-amber-300" role="status">
      The cloud browser session expired. Start a new approved session from Connected Apps to continue.</p> :
      <button type="button" onClick={show} disabled={busy}
        className="rounded-xl bg-brand text-white px-4 py-2.5 font-bold text-sm disabled:opacity-60">
        <Eye size={16} className="inline mr-1.5"/>{busy ? "Checking session…" : open ? "Hide live screen" : "Watch EARNOS work on screen"}
      </button>}
    {error ? <p role="alert" className="text-xs text-amber-300">{error}</p> : null}
    {open && ownsView && !expired ? <>
      <div className="w-full aspect-[4/3] sm:aspect-video overflow-hidden rounded-xl border border-line bg-black"
        data-testid="earnos-real-provider-live-view">
        <iframe title="Live authorized browser session" src={live.url}
          className="w-full h-full border-0"
          sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
          referrerPolicy="no-referrer"/>
      </div>
      <a href={live.url} target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-1.5 text-sm font-bold text-brand-light">
        Open full-size secure browser <ExternalLink size={16}/>
      </a>
      <p className="text-[11px] text-ink-2 flex items-start gap-1.5">
        <ShieldCheck size={14} className="shrink-0 mt-0.5"/>This is the provider's real live view.
        If embedding is blocked, use the full-size link. Login and external
        actions still require your permission; a live screen is not proof of sales.
      </p>
    </> : null}
  </section>;
}
